import { AuthedLayout } from "@/layouts/auth";
import { VStack, Center } from "@chakra-ui/react";
import { Divider } from "@/components/essentials/divider";
import { type NextPage } from "next";
import { AccountSettings } from "./__components__/account-settings";
import { OthersSettings } from "./__components__/others-settings";
import { VariousSettings } from "./__components__/various-settings";
import { ContractSettingList } from "./__components__/contract-settings/ContractSettingList";
import { useApolloError } from "@/hooks/useApolloError";
import { Skeleton } from "@/components/fragments/apollo/loader/skeleton";
import { useGetSettingsPagesFamilyCustomerQuery } from "./__gql__/get-family-customer.gen";
import { useGetSettingsPagesMeQuery } from "./__gql__/get-me.gen";
import { Typography } from "@/components/essentials/typography";
import { NextSeo } from "next-seo";
import {
  indexVariousTitleTestId,
  quitApplicationTextButtonTestId,
  skeletonAccountTestId,
  skeletonAddressesTestId,
  skeletonContractsTestId,
  skeletonQuitApplicationTestId,
} from "./const";
import { TextButton } from "@/components/essentials/button";
import { useGetQuitApplicationPagesSettingsQuery } from "./__gql__/get-quit-application.gen";
import { useMemo } from "react";
import {
  toysubAutoQuitApplicationWorkflowId,
  toysubCSQuitApplicationWorkflowId,
  toysubNotEligibleForPromotionQuitApplicationWorkflowId,
} from "@/configs/externalUrls";
import { QuitApplicationApplicableStatus } from "@/gql/generated/graphql.gen";
import { FAMILY_CUSTOMER_STATUS_QUIT_APPLICATION_SUBMITTED } from "@/configs/uuids/family-customer-status/familyCustomerStatus";
import { useCustomerContentPolicy } from "@/hooks/useCustomerContentPolicy";

const SettingsPage: NextPage = () => {
  const resultFamilyCustomerQuery = useGetSettingsPagesFamilyCustomerQuery();
  const resultMeQuery = useGetSettingsPagesMeQuery();
  const { isBabyRental } = useCustomerContentPolicy();
  const resultQuitApplicationQuery = useGetQuitApplicationPagesSettingsQuery({
    skip:
      resultFamilyCustomerQuery.loading ||
      resultFamilyCustomerQuery.data?.familyCustomer.familyCustomerStatus?.uuid ===
        FAMILY_CUSTOMER_STATUS_QUIT_APPLICATION_SUBMITTED ||
      !resultFamilyCustomerQuery.data?.familyCustomer.familyCustomerContractedPlans ||
      resultFamilyCustomerQuery.data.familyCustomer.familyCustomerContractedPlans.filter(
        (familyCustomerContractedPlan) => familyCustomerContractedPlan.isActive,
      ).length >= 2,
  });

  const quitApplicationWorkflowId: string | undefined = useMemo(() => {
    if (
      resultQuitApplicationQuery.data?.quitApplication.applicableStatus ===
      QuitApplicationApplicableStatus.NotEligibleForPromotion
    ) {
      return toysubNotEligibleForPromotionQuitApplicationWorkflowId;
    }

    return resultMeQuery.data?.me.isContractor &&
      resultQuitApplicationQuery.data?.quitApplication.applicableStatus === QuitApplicationApplicableStatus.Regular
      ? toysubAutoQuitApplicationWorkflowId
      : toysubCSQuitApplicationWorkflowId;
  }, [resultMeQuery.data?.me.isContractor, resultQuitApplicationQuery.data?.quitApplication.applicableStatus]);

  const isQuitApplicationSubmitted = useMemo(
    () =>
      resultFamilyCustomerQuery.data?.familyCustomer.familyCustomerStatus?.uuid ===
      FAMILY_CUSTOMER_STATUS_QUIT_APPLICATION_SUBMITTED,
    [resultFamilyCustomerQuery.data?.familyCustomer],
  );

  const onClickQuitApplicationButton: () => void = () => {
    // チャネルトークのチャットを開く
    if (typeof window !== "undefined" && window.ChannelIO) {
      window.ChannelIO("openWorkflow", quitApplicationWorkflowId);
    }
  };

  useApolloError(resultFamilyCustomerQuery.error || resultMeQuery.error || resultQuitApplicationQuery.error);
  return (
    <>
      <NextSeo title="設定メニュー" description="マイページの各種設定メニューに移動できます。" />
      <AuthedLayout data-testid="pages/settings" bgColor="light.main" px="1rem" py="1.5rem">
        <VStack
          spacing={5}
          py="1rem"
          px={{
            base: "0rem",
            sm: "0rem",
            md: "1rem",
            lg: "1.5rem",
            xl: "1.5rem",
            "2xl": "1.5rem",
          }}
          flexDir="column"
          justifyContent="center"
          alignItems="start"
        >
          {resultFamilyCustomerQuery.loading || resultMeQuery.loading ? (
            <Center py="2rem" w="100%">
              <Skeleton data-testid={skeletonAccountTestId} h="3rem" w="100%" />
            </Center>
          ) : (
            <AccountSettings
              familyCustomerId={resultFamilyCustomerQuery.data?.familyCustomer.id}
              isReceiver={resultMeQuery.data?.me.isReceiver}
              isMfaActive={resultMeQuery.data?.me.isMfaActive}
            />
          )}
          <Divider variant="light" w="100%" />
          {!isBabyRental && (
            <>
              <Typography data-testid={indexVariousTitleTestId} variant="display7" color="primary.main">
                各種設定
              </Typography>
              {resultMeQuery.loading ? (
                <Center py="2rem" w="100%">
                  <Skeleton data-testid={skeletonAddressesTestId} h="3rem" w="100%" />
                </Center>
              ) : (
                <VariousSettings
                  isView={{
                    contract: resultMeQuery.data?.me.isContractor,
                  }}
                />
              )}
              {resultFamilyCustomerQuery.loading || resultMeQuery.loading ? (
                <Center py="2rem" w="100%">
                  <Skeleton data-testid={skeletonContractsTestId} h="5rem" w="100%" />
                </Center>
              ) : (
                resultMeQuery.data?.me.isContractor === true && (
                  <ContractSettingList
                    familyCustomerContractedPlans={
                      resultFamilyCustomerQuery.data?.familyCustomer.familyCustomerContractedPlans
                    }
                  />
                )
              )}
            </>
          )}
          <Divider variant="light" w="100%" />
          <OthersSettings />

          {resultFamilyCustomerQuery.loading || resultMeQuery.loading ? (
            <Center py="2rem" w="100%">
              <Skeleton data-testid={skeletonQuitApplicationTestId} h="5rem" w="100%" />
            </Center>
          ) : (
            !isQuitApplicationSubmitted && (
              <Center w="100%" pt={10} pb={4}>
                <TextButton
                  size="sm"
                  color="gray.dark"
                  onClick={onClickQuitApplicationButton}
                  data-testid={quitApplicationTextButtonTestId}
                >
                  退会の申請はこちら
                </TextButton>
              </Center>
            )
          )}
        </VStack>
      </AuthedLayout>
    </>
  );
};

export default SettingsPage;
